import React, { useEffect, useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import InputV2 from "../basics/input/InputV2"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faDollarSign,
	faExclamationTriangle,
	faGripVertical,
	faHandshake,
	faLock,
	faTrash,
} from "@fortawesome/free-solid-svg-icons"
import * as Addons from "../../utilities/Addons"
import { generalStyles } from "../styles/styles"
import { COLORS } from "../../utilities/Constants"
import { useGroups } from "../../hooks/groups"
import { useAtelierPermissions } from "../../hooks/ateliers"
import {
	ATELIER_LIGNE_TYPE_AUTRE,
	ATELIER_LIGNE_TYPE_FOURNITURE,
	PAYE_PAR_ASSURANCE,
	PAYE_PAR_CLIENT,
	PAYE_PAR_MASTER,
} from "./AtelierLigneV2"

/**
 * Ligne d'atelier — rendu V2 en CSS grid, dédié à AtelierV2.
 *
 * Chaque ligne ET l'en-tête de section (AtelierLigneHeader) partagent le même
 * `gridTemplateColumns` (cf. buildColConfig) : l'alignement des colonnes est donc
 * garanti, y compris quand une ligne porte une colonne conditionnelle (Numéro,
 * Payé) que d'autres n'ont pas — la cellule absente réserve sa piste à vide.
 *
 * Le drag/drop est géré par le hook useSortableLignes (Pointer Events) ; la ligne
 * ne fait que consommer setNode / getHandleProps / getRowStyle.
 */

// --- Contrat de grille partagé ------------------------------------------------

// Largeur de chaque piste de colonne. Le label prend l'espace restant ; les
// colonnes numériques/actions sont fixes pour rester alignées d'une ligne à
// l'autre.
const COL_TRACKS = {
	grip: "18px",
	label: "minmax(150px, 1fr)",
	numero: "104px",
	valide: "48px",
	qte: "62px",
	prixAchat: "120px",
	prixVente: "122px",
	rabais: "104px",
	sousTotal: "118px",
	payePar: "120px",
	paye: "46px",
	modifierPrix: "32px",
	supprimer: "32px",
}

const COL_LABELS = {
	label: "Désignation",
	numero: "Numéro",
	valide: "Validé",
	qte: "Qté",
	prixAchat: "Prix achat",
	prixVente: "Prix vente",
	rabais: "Rabais",
	sousTotal: "Sous-total HT",
	payePar: "Payé par",
	paye: "Payé",
}

const NUMERIC_COLS = new Set([
	"qte",
	"prixAchat",
	"prixVente",
	"rabais",
	"sousTotal",
])
const CENTER_COLS = new Set(["valide", "paye"])

/**
 * Construit l'ordre de colonnes + le gridTemplateColumns d'une section à partir
 * de flags décidés UNE fois par section (pas par ligne).
 */
export const buildColConfig = (flags) => {
	const keys = ["grip", "label"]
	if (flags.numero) keys.push("numero")
	if (flags.valide) keys.push("valide")
	keys.push("qte")
	if (flags.prixAchat) keys.push("prixAchat")
	if (flags.prixVente) keys.push("prixVente")
	if (flags.rabais) keys.push("rabais")
	keys.push("sousTotal")
	if (flags.payePar) keys.push("payePar")
	if (flags.paye) keys.push("paye")
	if (flags.modifierPrix) keys.push("modifierPrix")
	keys.push("supprimer")
	return {
		keys,
		gridTemplateColumns: keys.map((k) => COL_TRACKS[k]).join(" "),
	}
}

const headerCellAlign = (key) =>
	NUMERIC_COLS.has(key) ? "right" : CENTER_COLS.has(key) ? "center" : "left"

/** En-tête de section : une rangée de labels alignée sur la même grille. */
export const AtelierLigneHeader = ({ colConfig }) => (
	<div
		style={{
			display: "grid",
			gridTemplateColumns: colConfig.gridTemplateColumns,
			columnGap: 8,
			alignItems: "end",
			padding: "0 4px 7px",
			borderBottom: `1px solid ${COLORS.border}`,
		}}
	>
		{colConfig.keys.map((key) => (
			<div
				key={key}
				style={{
					fontSize: 10,
					fontWeight: 600,
					letterSpacing: "0.06em",
					textTransform: "uppercase",
					color: "#000",
					textAlign: headerCellAlign(key),
					...(key === "sousTotal" ? { paddingLeft: 10 } : {}),
				}}
			>
				{COL_LABELS[key] || ""}
			</div>
		))}
	</div>
)

// --- Styles de cellules -------------------------------------------------------

const cellInput = { ...generalStyles.input, flex: undefined, width: "100%" }
const cellNum = { ...cellInput, textAlign: "right" }
const cellSelect = { ...generalStyles.select, flex: undefined, width: "100%" }

// Input « nu » à l'intérieur d'un input-group bordé (avec sa bascule HT/TTC).
const numInputBare = {
	flex: 1,
	minWidth: 0,
	padding: "7px 10px",
	border: "none",
	outline: "none",
	fontSize: 13,
	color: COLORS.text,
	backgroundColor: "transparent",
	fontFamily: "inherit",
	textAlign: "right",
	fontVariantNumeric: "tabular-nums",
	boxSizing: "border-box",
}

// Input « nu » du label, à l'intérieur de la boîte bordée qui accueille le badge
// sous-traitant collé à droite (même hauteur/typo que generalStyles.input).
const labelInputBare = {
	flex: 1,
	minWidth: 0,
	padding: "7px 11px",
	border: "none",
	outline: "none",
	fontSize: 13,
	fontWeight: 500,
	color: COLORS.text,
	backgroundColor: "transparent",
	fontFamily: "inherit",
	boxSizing: "border-box",
}

// Input-group : boîte bordée unique contenant la bascule (gauche) + l'input nu.
const ToggleField = ({ highlight, children }) => (
	<div
		className="atelier-toggle-group"
		style={{
			display: "flex",
			alignItems: "stretch",
			width: "100%",
			minWidth: 0,
			border: `1px solid ${highlight ? COLORS.gold : COLORS.border}`,
			borderRadius: 8,
			overflow: "hidden",
			backgroundColor: COLORS.surface,
		}}
	>
		{children}
	</div>
)

// Segment de bascule (HT/TTC, HT/%), collé à gauche dans l'input-group.
const ToggleAffix = ({ active, onClick, disabled, children }) => (
	<button
		type="button"
		onClick={onClick}
		disabled={disabled}
		style={{
			flexShrink: 0,
			minWidth: 32,
			padding: "0 7px",
			border: "none",
			borderRight: `1px solid ${COLORS.border}`,
			backgroundColor: active ? COLORS.text : COLORS.bg,
			color: active ? "#fff" : "#000",
			fontSize: 11,
			fontWeight: 600,
			cursor: disabled ? "default" : "pointer",
			fontFamily: "inherit",
		}}
	>
		{children}
	</button>
)

// --- Ligne --------------------------------------------------------------------

const AtelierLigneRow = ({
	atelierLigne,
	atelier,
	colConfig,
	updateAtelierLignes,
	updateLigneFull,
	removeAtelierLignes,
	disableLigne,
	isSVP,
	onModifierPrix = null,
	autoFocus = false,
	onAutoFocused,
	// drag (useSortableLignes)
	setNode,
	getHandleProps,
	getRowStyle,
	isDragging,
}) => {
	const { fournisseurs } = useSelector((state) => state.autreInfo)
	const fournitures = useSelector((state) => state.atelierInfo.fournitures)
	const { isBoss, isAdmin } = useGroups()
	const { canSeeAdmin } = useAtelierPermissions()

	const tva = atelier.tva
	const id = atelierLigne.id

	const getSousTraitant = useMemo(
		() => fournisseurs.find((f) => f.id === atelierLigne.sousTraitant),
		[atelierLigne.sousTraitant, fournisseurs],
	)

	const getSousTotal = useMemo(() => {
		const { quantite, prixVente, isHT } = atelierLigne
		if (Number(quantite) && Number(prixVente)) {
			if (isHT) return quantite * prixVente
			return quantite * Addons.TTCtoHT(prixVente, tva)
		}
		return 0
	}, [atelierLigne, tva])

	const getSousTotalRabais = useMemo(() => {
		const { rabais, rabaisIsFlat, quantite } = atelierLigne
		if (rabaisIsFlat) return getSousTotal - rabais * quantite
		return getSousTotal * (1 - rabais / 100)
	}, [atelierLigne, getSousTotal])

	const getSousTotalAchat = useMemo(() => {
		// Flag ACHAT (isAchHT), pas le flag vente (isHT) : une ligne article
		// débitée a un prix d'achat HT (isAchHT=true) avec isHT=false — l'ancien
		// test le divisait une fois de trop (même correctif que AtelierLigneV2).
		const { quantite, prixAchat, isAchHT } = atelierLigne
		if (Number(quantite) && Number(prixAchat)) {
			if (isAchHT) return quantite * prixAchat
			return quantite * Addons.TTCtoHT(prixAchat, tva)
		}
		return 0
	}, [atelierLigne, tva])

	const toggleTaxe = () => {
		const al = { ...atelierLigne }
		al.prixVente = al.isHT
			? Addons.HTtoTTC(al.prixVente, tva)
			: Addons.TTCtoHT(al.prixVente, tva)
		al.isHT = !al.isHT
		updateLigneFull(al)
	}
	const toggleAchatTaxe = () => {
		const al = { ...atelierLigne }
		al.prixAchat = al.isAchHT
			? Addons.HTtoTTC(al.prixAchat, tva)
			: Addons.TTCtoHT(al.prixAchat, tva)
		al.isAchHT = !al.isAchHT
		updateLigneFull(al)
	}
	const toggleRabais = () => {
		const al = { ...atelierLigne }
		al.rabais = Addons.round100(
			al.rabaisIsFlat
				? (al.rabais / getSousTotal) * 100
				: (getSousTotal * al.rabais) / 100,
		)
		al.rabaisIsFlat = !al.rabaisIsFlat
		updateLigneFull(al)
	}
	const updatePrixVente = (e) => {
		const al = { ...atelierLigne }
		al.prixVente = e.target.value
		if (atelierLigne.fourniture && atelierLigne.fourniture !== 0) {
			const four = fournitures.find(
				(f) => f.id === atelierLigne.fourniture,
			)
			let prixAchat = 0
			if (e.target.value !== "" && four) {
				prixAchat =
					four.operation === 0
						? parseFloat(e.target.value) - four.valeur
						: Addons.round100(
								(parseFloat(e.target.value) *
									(100 - four.valeur)) /
									100,
							)
			}
			al.prixAchat = prixAchat
		}
		updateLigneFull(al)
	}

	const lockedAchat =
		atelierLigne.article > 0 ||
		atelierLigne.articleVirtuel > 0 ||
		atelierLigne.fourniturePaid ||
		disableLigne ||
		atelierLigne.fourniture > 0

	// Ligne « dérivée » (liée à un article / fournisseur payé) : champs prix non
	// éditables → fond atténué pour signaler « calculé, n'y touche pas ».
	const isDerived =
		atelierLigne.article > 0 ||
		atelierLigne.articleVirtuel > 0 ||
		atelierLigne.fourniturePaid

	const showPaye =
		canSeeAdmin &&
		(atelierLigne.sousTraitant > 0 ||
			[ATELIER_LIGNE_TYPE_AUTRE, ATELIER_LIGNE_TYPE_FOURNITURE].includes(
				atelierLigne.type,
			))

	const handleProps = getHandleProps(id)

	// --- Chaîne de focus Entrée (Maj+Entrée = précédent) ---------------------
	// Refs des champs éditables + ordre dynamique = sous-liste des colonnes
	// présentes (colConfig) ET activées pour cette ligne. nextOf/prevOf donnent
	// la ref voisine pour InputV2 (nextRef / prevRef).
	const refLabel = useRef(null)
	const refQte = useRef(null)
	const refPrixAchat = useRef(null)
	const refPrixVente = useRef(null)
	const refRabais = useRef(null)
	const focusOrder = useMemo(() => {
		const present = new Set(colConfig.keys)
		const labelDisabled =
			atelierLigne.article > 0 ||
			atelierLigne.fourniturePaid ||
			disableLigne
		const blocked = atelierLigne.fourniturePaid || disableLigne
		const prixVenteDisabled =
			atelierLigne.article > 0 ||
			atelierLigne.articleVirtuel > 0 ||
			blocked
		return [
			{
				key: "label",
				ref: refLabel,
				on: present.has("label") && !labelDisabled,
			},
			{ key: "qte", ref: refQte, on: present.has("qte") && !blocked },
			{
				key: "prixAchat",
				ref: refPrixAchat,
				on: present.has("prixAchat") && !lockedAchat,
			},
			{
				key: "prixVente",
				ref: refPrixVente,
				on: present.has("prixVente") && !prixVenteDisabled,
			},
			{
				key: "rabais",
				ref: refRabais,
				on: present.has("rabais") && !blocked,
			},
		].filter((c) => c.on)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		colConfig.keys,
		atelierLigne.article,
		atelierLigne.articleVirtuel,
		atelierLigne.fourniturePaid,
		disableLigne,
		lockedAchat,
	])
	const nextOf = (key) => {
		const i = focusOrder.findIndex((c) => c.key === key)
		return i >= 0 && i < focusOrder.length - 1
			? focusOrder[i + 1].ref
			: undefined
	}
	const prevOf = (key) => {
		const i = focusOrder.findIndex((c) => c.key === key)
		return i > 0 ? focusOrder[i - 1].ref : undefined
	}

	// Focus du 1er champ focusable à l'ajout d'une ligne (clic → saisie clavier).
	useEffect(() => {
		if (autoFocus && focusOrder.length > 0 && focusOrder[0].ref.current) {
			focusOrder[0].ref.current.focus()
			if (onAutoFocused) onAutoFocused()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// --- Cellules par clé de colonne -----------------------------------------
	const renderCell = (key) => {
		switch (key) {
			case "grip":
				return (
					<div
						key="grip"
						className={
							disableLigne ? "al-grip al-grip--locked" : "al-grip"
						}
						{...handleProps}
						title={disableLigne ? "Verrouillé" : "Déplacer"}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: COLORS.textLight,
							...(handleProps.style || {}),
						}}
					>
						<FontAwesomeIcon
							icon={disableLigne ? faLock : faGripVertical}
						/>
					</div>
				)

			case "label": {
				const labelDisabled =
					atelierLigne.article > 0 ||
					atelierLigne.fourniturePaid ||
					disableLigne
				const hasSousTraitant =
					atelierLigne.sousTraitant > 0 && getSousTraitant
				// Flags posés par le back (serializer) : à ≥ 25 débit de stock
				// incomplet (reliquat historique, la clôture est bloquée tant
				// qu'il est levé) ; en 15/20 pièce non couverte par le stock
				// disponible (« Travaux terminés » sera refusé).
				const stockFlag = atelierLigne.debit_incomplet
					? {
							label: "Stock",
							title:
								"Stock non débité : quantité débitée partiellement " +
								"(stock insuffisant). Le reste sera débité au " +
								"prochain enregistrement après la réception de " +
								"commande ; la clôture est bloquée d'ici là.",
						}
					: atelierLigne.piece_a_commander
						? {
								label: "À commander",
								title:
									"Pièce à commander : le stock disponible ne couvre " +
									"pas la quantité. « Travaux terminés » sera refusé " +
									"tant que la réception de commande n'est pas saisie " +
									"dans l'inventaire.",
							}
						: null
				const hasBadges = hasSousTraitant || !!stockFlag
				const labelInput = (
					<InputV2
						ref={refLabel}
						nextRef={nextOf("label")}
						prevRef={prevOf("label")}
						style={
							hasBadges
								? labelInputBare
								: {
										...cellInput,
										color: "#000",
									}
						}
						value={atelierLigne.label}
						onChange={(e) =>
							updateAtelierLignes(e.target.value, "label", id)
						}
						disabled={labelDisabled}
					/>
				)
				if (!hasBadges)
					return (
						<div key="label" style={{ minWidth: 0 }}>
							{labelInput}
						</div>
					)
				return (
					<div
						key="label"
						style={{
							display: "flex",
							alignItems: "center",
							width: "100%",
							minWidth: 0,
							border: `1px solid ${COLORS.border}`,
							borderRadius: 8,
							overflow: "hidden",
							backgroundColor: labelDisabled
								? "#FAFAF9"
								: COLORS.surface,
						}}
					>
						{labelInput}
						{stockFlag && (
							<span
								title={stockFlag.title}
								style={{
									flexShrink: 0,
									display: "inline-flex",
									alignItems: "center",
									gap: 5,
									margin: "0 6px",
									padding: "2px 8px",
									borderRadius: 99,
									fontSize: 10,
									fontWeight: 600,
									color: COLORS.warning,
									backgroundColor: COLORS.warningLight,
									border: `1px solid ${COLORS.warning}`,
								}}
							>
								<FontAwesomeIcon
									icon={faExclamationTriangle}
									style={{ fontSize: 9 }}
								/>
								{stockFlag.label}
							</span>
						)}
						{hasSousTraitant && (
							<span
								title={getSousTraitant.label}
								style={{
									flexShrink: 0,
									display: "inline-flex",
									alignItems: "center",
									gap: 5,
									maxWidth: "50%",
									margin: "0 6px",
									padding: "2px 8px",
									borderRadius: 99,
									fontSize: 10,
									fontWeight: 500,
									color: COLORS.textMuted,
									backgroundColor: COLORS.bg,
									border: `1px solid ${COLORS.border}`,
								}}
							>
								<FontAwesomeIcon
									icon={faHandshake}
									style={{ fontSize: 9 }}
								/>
								<span
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{getSousTraitant.label}
								</span>
							</span>
						)}
					</div>
				)
			}

			case "numero":
				return (
					<div
						key="numero"
						style={{ display: "flex", alignItems: "center" }}
					>
						{atelierLigne.articleVirtuel > 0 && (
							<span
								title={`Article inventaire nº ${atelierLigne.articleVirtuelNumero}`}
								style={{
									display: "inline-flex",
									alignItems: "center",
									maxWidth: "100%",
									padding: "4px 10px",
									borderRadius: 7,
									fontSize: 11,
									fontWeight: 600,
									color: COLORS.accent,
									backgroundColor: COLORS.accentLight,
									border: `1px solid ${COLORS.accent}22`,
									fontVariantNumeric: "tabular-nums",
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{atelierLigne.articleVirtuelNumero}
							</span>
						)}
					</div>
				)

			case "valide":
				return (
					<div
						key="valide"
						style={{ display: "flex", justifyContent: "center" }}
					>
						<input
							type="checkbox"
							checked={atelierLigne.valide}
							onChange={() =>
								updateAtelierLignes(
									!atelierLigne.valide,
									"valide",
									id,
								)
							}
							disabled={disableLigne}
						/>
					</div>
				)

			case "qte":
				return (
					<InputV2
						key="qte"
						ref={refQte}
						nextRef={nextOf("qte")}
						prevRef={prevOf("qte")}
						style={cellNum}
						value={atelierLigne.quantite}
						onChange={(e) =>
							updateAtelierLignes(e.target.value, "quantite", id)
						}
						onKeyDown={(e) => Addons.typeNumber(e)}
						onFocus={(e) => e.target.select()}
						disabled={atelierLigne.fourniturePaid || disableLigne}
					/>
				)

			case "prixAchat":
				return (
					<div key="prixAchat">
						<ToggleField>
							<ToggleAffix
								active={!atelierLigne.isAchHT}
								onClick={toggleAchatTaxe}
								disabled={
									atelierLigne.fourniturePaid || disableLigne
								}
							>
								{atelierLigne.isAchHT ? "HT" : "TTC"}
							</ToggleAffix>
							<InputV2
								ref={refPrixAchat}
								nextRef={nextOf("prixAchat")}
								prevRef={prevOf("prixAchat")}
								style={numInputBare}
								value={atelierLigne.prixAchat}
								onChange={(e) =>
									updateAtelierLignes(
										e.target.value,
										"prixAchat",
										id,
									)
								}
								onKeyDown={(e) => Addons.typeNumber(e)}
								onFocus={(e) => e.target.select()}
								disabled={lockedAchat}
							/>
						</ToggleField>
					</div>
				)

			case "prixVente":
				return (
					<div key="prixVente">
						<ToggleField highlight={atelierLigne.prixVenteModifie}>
							<ToggleAffix
								active={!atelierLigne.isHT}
								onClick={toggleTaxe}
								disabled={
									atelierLigne.fourniturePaid || disableLigne
								}
							>
								{atelierLigne.isHT ? "HT" : "TTC"}
							</ToggleAffix>
							{atelierLigne.article > 0 ? (
								<input
									style={{
										...numInputBare,
										backgroundColor: "#FAFAF9",
									}}
									value={Addons.round100(
										atelierLigne.prixVente,
									)}
									readOnly
								/>
							) : (
								<InputV2
									ref={refPrixVente}
									nextRef={nextOf("prixVente")}
									prevRef={prevOf("prixVente")}
									style={numInputBare}
									value={atelierLigne.prixVente}
									onChange={updatePrixVente}
									onKeyDown={(e) => Addons.typeNumber(e)}
									onFocus={(e) => e.target.select()}
									readOnly={
										atelierLigne.articleVirtuel > 0 ||
										atelierLigne.fourniturePaid ||
										disableLigne
									}
								/>
							)}
						</ToggleField>
					</div>
				)

			case "rabais":
				return (
					<div key="rabais">
						<ToggleField>
							<ToggleAffix
								active={!atelierLigne.rabaisIsFlat}
								onClick={toggleRabais}
								disabled={
									atelierLigne.fourniturePaid || disableLigne
								}
							>
								{atelierLigne.rabaisIsFlat ? "HT" : "%"}
							</ToggleAffix>
							<InputV2
								ref={refRabais}
								nextRef={nextOf("rabais")}
								prevRef={prevOf("rabais")}
								style={numInputBare}
								value={atelierLigne.rabais}
								onChange={(e) =>
									updateAtelierLignes(
										e.target.value,
										"rabais",
										id,
									)
								}
								onKeyDown={(e) => Addons.typeNumber(e)}
								onFocus={(e) => e.target.select()}
								disabled={
									atelierLigne.fourniturePaid || disableLigne
								}
							/>
						</ToggleField>
					</div>
				)

			case "sousTotal":
				return (
					<div
						key="sousTotal"
						style={{
							paddingLeft: 10,
							borderLeft: `1px solid ${COLORS.border}`,
							textAlign: "right",
							fontSize: 13,
							fontWeight: 600,
							color: COLORS.text,
							fontVariantNumeric: "tabular-nums",
						}}
					>
						{Addons.round100(
							isSVP ? getSousTotalAchat : getSousTotalRabais,
						)}
					</div>
				)

			case "payePar":
				return (
					<select
						key="payePar"
						style={cellSelect}
						value={atelierLigne.payePar}
						onChange={(e) =>
							updateAtelierLignes(e.target.value, "payePar", id)
						}
						disabled={atelierLigne.fourniturePaid || disableLigne}
					>
						<option value={PAYE_PAR_ASSURANCE}>Assurance</option>
						<option value={PAYE_PAR_CLIENT}>Client</option>
						<option value={PAYE_PAR_MASTER}>Perte</option>
					</select>
				)

			case "paye":
				return (
					<div
						key="paye"
						style={{ display: "flex", justifyContent: "center" }}
					>
						{showPaye && (
							<input
								type="checkbox"
								checked={atelierLigne.fourniturePaid}
								onChange={() =>
									updateAtelierLignes(
										!atelierLigne.fourniturePaid,
										"fourniturePaid",
										id,
									)
								}
								disabled={disableLigne || !(isBoss || isAdmin)}
							/>
						)}
					</div>
				)

			case "modifierPrix":
				return (
					<button
						key="modifierPrix"
						type="button"
						title="Modifier le prix de vente"
						style={generalStyles.iconBtn(COLORS.gold)}
						onClick={() => onModifierPrix(atelierLigne)}
						disabled={
							(!atelier.isAssurance &&
								atelierLigne.article === 0 &&
								atelierLigne.articleVirtuel === 0) ||
							disableLigne ||
							!Number.isInteger(atelier.id) ||
							!Number.isInteger(id)
						}
					>
						<FontAwesomeIcon icon={faDollarSign} />
					</button>
				)

			case "supprimer":
				return (
					<button
						key="supprimer"
						type="button"
						title="Supprimer la ligne"
						style={generalStyles.iconBtn(COLORS.danger)}
						onClick={() => removeAtelierLignes(atelierLigne)}
						disabled={disableLigne}
					>
						<FontAwesomeIcon icon={faTrash} />
					</button>
				)

			default:
				return <div key={key} />
		}
	}

	return (
		<div
			ref={setNode(id)}
			className="al-row"
			data-dragging={isDragging ? "true" : undefined}
			style={{
				display: "grid",
				gridTemplateColumns: colConfig.gridTemplateColumns,
				columnGap: 8,
				alignItems: "center",
				padding: "8px 4px",
				borderBottom: `1px solid ${COLORS.border}`,
				borderRadius: isDragging ? 10 : 0,
				backgroundColor: isDragging
					? COLORS.surface
					: isDerived
						? "rgba(28, 25, 23, 0.022)"
						: "transparent",
				...getRowStyle(id),
			}}
		>
			{colConfig.keys.map(renderCell)}
		</div>
	)
}

export default AtelierLigneRow
